import { useEffect, useState } from "react";
import Image from "next/image";
import LazyImage from "@/my_components/Common/LazyImage";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import logo from "../../public/images/logo/smarttech-full-blue.png";
import logoLight from "../../public/images/logo/smarttech-full-white.png";

import { useAppContext } from "@/context/Context";
import { useProfile } from "@/context/ProfileContext";
import { CONFIG } from "@/app_config.js";
import { ROUTE } from "@/route/app_routes.js";
import { ADMIN_NAV_ITEMS, STUDENT_NAV_ITEMS } from "@/my_data/dashboard/navConfig";
import { getCategoryMenu } from "@/services/courseService";
import { getUnreadCount } from "@/services/notificationService";
import { logoutUser } from "@/services/authService";
import { SUPPORTED_LANGUAGES, changeLanguage } from "@/lib/i18n";

/**
 * Mobile off-canvas menu -- rebuilt to carry everything the desktop navbar can do, which the
 * previous version (logo/blurb/contact links/a single "All Events" nav item/an Enroll button/
 * social icons) left out entirely: category browsing, search, the full auth-aware account menu
 * (dashboard nav + notifications + settings + logout, or Login/Join when logged out), and the
 * language switcher. Structured as labeled accordion sections (Material-style: one section open
 * at a time, clear touch targets, no hover-dependent interactions -- the desktop Category mega-
 * menu and language dropdown both rely on :hover/positioned-panel patterns that don't translate
 * to a narrow touch surface, so this reimplements their *content* against a layout that does).
 */
const MobileMenu = () => {
  const { mobile, setMobile, isLightTheme, setSearch } = useAppContext();
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const { profile, loggedIn, loading: profileLoading, refreshProfile } = useProfile();

  const [openSection, setOpenSection] = useState("account");
  const [categories, setCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const close = () => setMobile(true);

  useEffect(() => {
    if (!mobile) return; // `mobile` is true when the panel is CLOSED (see the className below)
    getCategoryMenu(0)
      .then((res) => setCategories(res.data || []))
      .catch(() => {})
      .finally(() => setCategoriesLoaded(true));
  }, [mobile]);

  useEffect(() => {
    if (!loggedIn) return;
    getUnreadCount()
      .then((res) => setUnreadCount(res.data?.unread_count ?? 0))
      .catch(() => {});
  }, [loggedIn]);

  const openSearch = () => {
    close();
    setSearch(false); // Search.js's overlay shows when `search` is false -- see HeaderRight.js
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    close();
    try {
      await logoutUser();
    } catch (error) {
      // Cookie is cleared server-side regardless -- fall through to refreshing/redirecting.
    }
    await refreshProfile();
    toast.success(t("nav.loggedOutToast"));
    router.push(ROUTE.login);
  };

  const isAdmin = profile?.USER_ROLE === "admin";
  const dashboardItems = isAdmin ? ADMIN_NAV_ITEMS : STUDENT_NAV_ITEMS;
  const settingsRoute = isAdmin ? ROUTE.adminSettings : ROUTE.dashboardSettings;

  const toggleSection = (key) => setOpenSection((prev) => (prev === key ? null : key));

  return (
    <>
      <div className={`popup-mobile-menu ${mobile ? "" : "active"}`}>
        <div className="inner-wrapper rbt-mobile-menu-v2">
          <div className="inner-top">
            <div className="content">
              <div className="logo">
                <Link href="/" onClick={close}>
                  <Image
                    src={isLightTheme ? logo : logoLight}
                    width={148}
                    height={56}
                    priority
                    alt="SmartTech Academy"
                  />
                </Link>
              </div>
              <div className="rbt-btn-close">
                <button className="close-button rbt-round-btn" onClick={close} aria-label={t("nav.close", "Close menu")}>
                  <i className="feather-x"></i>
                </button>
              </div>
            </div>

            <button type="button" className="rbt-mobile-search-trigger" onClick={openSearch}>
              <i className="feather-search"></i>
              <span>{t("nav.searchPlaceholder", "What do you want to learn?")}</span>
            </button>
          </div>

          <div className="rbt-mobile-menu-body">
            {/* -- Account: dashboard/account destinations when signed in, Login/Join otherwise */}
            <div className="rbt-mm-section">
              <button
                type="button"
                className="rbt-mm-section__head"
                onClick={() => toggleSection("account")}
                aria-expanded={openSection === "account"}
              >
                <span>{loggedIn ? t("nav.myAccount", "My Account") : t("nav.accountAccess", "Account")}</span>
                <i className={`feather-chevron-down ${openSection === "account" ? "is-open" : ""}`}></i>
              </button>

              {openSection === "account" && (
                <div className="rbt-mm-section__body">
                  {!profileLoading && loggedIn ? (
                    <>
                      <Link href={ROUTE.dashboardProfile} className="rbt-mm-profile" onClick={close}>
                        <span className="rbt-mm-profile__avatar">
                          <LazyImage
                            src={profile?.DP || "/app_images/dashboard/avatar.webp"}
                            width={40}
                            height={40}
                            alt={t("nav.userAvatarAlt", "Your avatar")}
                          />
                        </span>
                        <span className="rbt-mm-profile__info">
                          <strong>{`${profile.FIRSTNAME} ${profile.LASTNAME}`.trim()}</strong>
                          <span>{t("nav.viewProfile", "View Profile")}</span>
                        </span>
                      </Link>

                      <ul className="rbt-mm-list">
                        {dashboardItems.map((item) => (
                          <li key={item.link}>
                            <Link href={item.link} onClick={close}>
                              <i className={item.icon}></i>
                              <span>{item.textKey ? t(item.textKey, item.text) : item.text}</span>
                              {item.link === ROUTE.notifications && unreadCount > 0 && (
                                <span className="rbt-mm-badge">{unreadCount}</span>
                              )}
                            </Link>
                          </li>
                        ))}
                        <li>
                          <Link href={settingsRoute} onClick={close}>
                            <i className="feather-settings"></i>
                            <span>{t("nav.settings", "Settings")}</span>
                          </Link>
                        </li>
                        <li>
                          <a href="#" onClick={handleLogout}>
                            <i className="feather-log-out"></i>
                            <span>{t("nav.logout", "Logout")}</span>
                          </a>
                        </li>
                      </ul>
                    </>
                  ) : (
                    <div className="rbt-mm-auth-buttons">
                      <Link className="rbt-btn btn-border-gradient radius-round btn-sm w-100 justify-content-center text-center" href={ROUTE.login} onClick={close}>
                        <span>{t("nav.logIn", "Log In")}</span>
                      </Link>
                      <Link className="rbt-btn btn-gradient radius-round btn-sm w-100 justify-content-center text-center" href={ROUTE.register} onClick={close}>
                        <span>{t("nav.joinForFree", "Join for Free")}</span>
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* -- Explore: category browsing (touch-friendly list, not the desktop hover mega-menu) */}
            <div className="rbt-mm-section">
              <button
                type="button"
                className="rbt-mm-section__head"
                onClick={() => toggleSection("explore")}
                aria-expanded={openSection === "explore"}
              >
                <span>{t("nav.explore", "Explore")}</span>
                <i className={`feather-chevron-down ${openSection === "explore" ? "is-open" : ""}`}></i>
              </button>

              {openSection === "explore" && (
                <div className="rbt-mm-section__body">
                  <ul className="rbt-mm-list">
                    <li>
                      <Link href={ROUTE.course} onClick={close}>
                        <i className="feather-grid"></i>
                        <span>{t("nav.allCourses", "All Courses")}</span>
                      </Link>
                    </li>
                    <li>
                      <Link href={ROUTE.events} onClick={close}>
                        <i className="feather-calendar"></i>
                        <span>{t("nav.allEvents", "All Events")}</span>
                      </Link>
                    </li>
                    {categoriesLoaded && categories.length === 0 ? null : (
                      categories.map((category) => (
                        <li key={category.slug}>
                          <Link href={`${ROUTE.course}?category=${encodeURIComponent(category.name)}`} onClick={close}>
                            <i className="feather-layers"></i>
                            <span>{category.name}</span>
                          </Link>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              )}
            </div>

            {/* -- Language: same pill-button switcher used in Settings' Appearance tab */}
            <div className="rbt-mm-section">
              <button
                type="button"
                className="rbt-mm-section__head"
                onClick={() => toggleSection("language")}
                aria-expanded={openSection === "language"}
              >
                <span>{t("settings.language.title", "Language")}</span>
                <i className={`feather-chevron-down ${openSection === "language" ? "is-open" : ""}`}></i>
              </button>

              {openSection === "language" && (
                <div className="rbt-mm-section__body">
                  <div className="rbt-lang-switcher rbt-lang-switcher--mobile" role="radiogroup" aria-label={t("settings.language.title", "Language")}>
                    {SUPPORTED_LANGUAGES.map((lang) => {
                      const isActive = i18n.language === lang.code;
                      return (
                        <button
                          type="button"
                          key={lang.code}
                          role="radio"
                          aria-checked={isActive}
                          className={`rbt-lang-switcher__option ${isActive ? "is-active" : ""}`}
                          onClick={() => changeLanguage(lang.code)}
                        >
                          {lang.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mobile-menu-bottom">
            <div className="rbt-btn-wrapper mb--20">
              <Link
                className="rbt-btn btn-border-gradient radius-round btn-sm hover-transform-none w-100 justify-content-center text-center"
                href={ROUTE.register}
                onClick={close}
              >
                <span>{t("nav.enrollNow", "Enroll Now")}</span>
              </Link>
            </div>

            <ul className="navbar-top-left rbt-information-list justify-content-start">
              <li>
                <Link href={`mailto:${CONFIG.EMAIL_1}`}>
                  <i className="feather-mail"></i>{CONFIG.EMAIL_1}
                </Link>
              </li>
              <li>
                <Link href={`tel:${CONFIG.PHONE_1}`}>
                  <i className="feather-phone"></i>{CONFIG.PHONE_1}
                </Link>
              </li>
            </ul>

            <div className="social-share-wrapper">
              <span className="rbt-short-title d-block">{t("nav.findWithUs", "Find With Us")}</span>
              <ul className="social-icon social-default transparent-with-border justify-content-start mt--20">
                <li>
                  <Link href={ROUTE.FACEBOOK} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                    <i className="feather-facebook"></i>
                  </Link>
                </li>
                <li>
                  <Link href={ROUTE.TWITTER} target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                    <i className="feather-twitter"></i>
                  </Link>
                </li>
                <li>
                  <Link href={ROUTE.INSTAGRAM} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                    <i className="feather-instagram"></i>
                  </Link>
                </li>
                <li>
                  <Link href={ROUTE.LINKEDIN} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                    <i className="feather-linkedin"></i>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileMenu;
